import React, { useState } from 'react';
import { ListBulletIcon } from '@heroicons/react/24/solid';
import { LockClosedIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
// import bgImage from '../../../assets/images/smallImg.jpg';
import { deleteForm, postCopyForm } from '../../../services/forms/formsService';
import { format } from 'date-fns';
import { Button } from '../../../components/forms/Buttons/Button';
import DOMPurify from 'dompurify';
import { FORM_STATUS, FORM_STATUS_LABELS, toastFormError } from '../formUtils';

// canManage — SSA: копирование и удаление. Остальным карточка только
// открывает редактор (кнопки не вложены в <a>, поэтому без preventDefault).
// nextYearTaken — на следующий год форма уже есть, копировать некуда.
export default function FomrCard({
  form,
  refreshData,
  canManage,
  nextYearTaken,
}) {
  const [isCopying, setIsCopying] = useState(false);
  const year = form?.exam_year_year;
  // Показываем только то, что бэк выполнит: на занятый год копия не
  // создастся (409), а форму прошлого года удалить нельзя (403).
  const canCopy = typeof year === 'number' && !nextYearTaken;
  const canDelete = !form.year_has_ended;
  // name — html из редактора, на карточке нужен только текст
  const name = DOMPurify.sanitize(form?.name ?? '', {
    USE_PROFILES: {
      html: false,
    },
  });

  const handleDeleteClick = () => {
    toast.dark(
      ({ closeToast }) => (
        <>
          <div>Удалить форму «{name}»?</div>
          <div className='mt-3 flex justify-center gap-3'>
            <Button
              text={'Да'}
              color='error-solid'
              onClick={() => {
                closeToast();
                deleteForm(form.uuid)
                  ?.then(() => {
                    refreshData();
                  })
                  ?.catch((err) => {
                    // 409 — на форму уже есть ответы
                    toastFormError(err, `Не удалось удалить форму «${name}»`);
                  });
              }}
            />
            <Button text={'Нет'} className='text-white' onClick={closeToast} />
          </div>
        </>
      ),
      {
        toastId: `delete-form-${form.uuid}`,
        autoClose: false,
        closeButton: true,
      },
    );
  };

  const handleCopyClick = () => {
    setIsCopying(true);
    postCopyForm(form.uuid)
      ?.then((res) => {
        toast.success(
          `Форма скопирована на ${res?.exam_year_year ?? year + 1} год`,
        );
        refreshData();
      })
      ?.catch((err) => {
        // 409 — следующего года нет, он закончился или форма на него уже есть
        toastFormError(err, 'Не удалось скопировать форму');
      })
      ?.finally(() => setIsCopying(false));
  };

  return (
    <div className='flex h-48 w-72 flex-col overflow-hidden rounded border shadow-md hover:border-violet-500'>
      {/* <img className='w-full' src={bgImage} alt='Placeholder' /> */}
      <a
        target='_blank'
        rel='noreferrer'
        href={`/forms/${form.uuid}/edit`}
        className='flex flex-1 flex-col justify-between gap-2 p-4'
      >
        <div className='flex flex-col gap-2'>
          <div className='truncate text-xl font-bold' title={name}>
            {name || 'Без названия'}
          </div>
          <div className='flex flex-row flex-wrap gap-1 text-xs font-semibold'>
            <span className='rounded bg-violet-100 px-2 py-0.5 text-violet-700'>
              {year ?? 'Без года'}
            </span>
            {/* форма прошлого года заморожена при любом статусе — статус
                не показываем, чтобы «Открыта» не спорило с замком */}
            {form.year_has_ended ? (
              <span className='flex flex-row items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-amber-800'>
                <LockClosedIcon className='h-3' />
                Прошлый год
              </span>
            ) : (
              FORM_STATUS_LABELS[form.status] && (
                <span
                  className={`rounded px-2 py-0.5 ${
                    form.status === FORM_STATUS.EDIT
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {FORM_STATUS_LABELS[form.status]}
                </span>
              )
            )}
          </div>
        </div>
        <div className='flex flex-row items-center gap-2 text-sm text-slate-500'>
          <div className='flex h-6 w-6 flex-col items-center justify-center rounded bg-violet-500'>
            <ListBulletIcon className='h-5 w-5 text-white' />
          </div>
          {form.created &&
            `создана: ${format(new Date(form.created), 'dd.MM.yyyy HH:mm')}`}
        </div>
      </a>
      {canManage && (canCopy || canDelete) && (
        <div className='flex flex-row items-center justify-between gap-1 border-t bg-white px-4 py-1'>
          {canCopy ? (
            <button
              type='button'
              className='rounded px-2 py-1 text-sm text-violet-700 hover:bg-violet-50 disabled:cursor-default disabled:text-slate-400'
              disabled={isCopying}
              onClick={handleCopyClick}
            >
              {`Копировать на ${year + 1}`}
            </button>
          ) : (
            <span />
          )}
          {canDelete && (
            <TrashIcon
              className='h-9 cursor-pointer rounded-full p-2 text-error hover:bg-red-100'
              title={'Удалить'}
              onClick={handleDeleteClick}
            />
          )}
        </div>
      )}
    </div>
  );
}
